import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useToggle} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {HttpResponseError} from '../../../../../../libs/shared/core/src/services/http/httpError';
import {BannerContent} from '../../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {InlineLinkButton} from '../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {BasicDialog} from '../../../../../../libs/shared/ui/src/components/dialogs/basicDialog';
import {OkCancelDialog} from '../../../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {generateMergeErrorMessage} from '../../conversation/mergeConversationHelpers';
import {StaticViewerBanner} from '../static/staticViewerBanner';
import {StaticViewerBannerAction} from '../static/staticViewerBannerAction';

const MAX_MESSAGE_COUNT_TO_MERGE = 100;

/*
 * Style.
 */

const StyledStaticViewerBanner = styled(StaticViewerBanner)`
  padding: 0 4px 0 12px;
`;

const StyledBannerContent = styled(BannerContent)`
  display: inline-block;
  font-size: ${fontSizes.verySmall};
  padding: 9px 0 9px 0;
  margin-bottom: 0;
`;

// Needed for BannerIcon to display inline.
const StyledInfoIconWrapperDiv = styled.div`
  display: inline-block;
  margin-bottom: -4px;
  margin-left: 6px;
`;

// Needed for BannerActions to be displayed inline.
const StyledActionButtonWrapperDiv = styled.div`
  display: inline-block;
  position: relative;
  grid-area: action;

  margin-left: 24px;
`;

const StyledStaticViewerBannerAction = styled(StaticViewerBannerAction)`
  &:not(:first-child) {
    margin-left: 2px;
  }

  &:hover {
    background-color: ${(p) => p.theme.palette.blue.shade30};
  }
`;

const StyledDarkTextSpan = styled.span`
  color: ${(p) => p.theme.palette.grey.shade50};
`;

const StyledActionTextSpan = styled.span`
  color: ${(p) => p.theme.palette.blue.shade50};
  text-transform: uppercase;
`;

const StyledTextStrong = styled.strong`
  font-weight: ${fontWeights.bold};
`;

const StyledTooltipTextDiv = styled.div`
  color: ${(p) => p.theme.greys.shade10};
  width: 200px;
  font-weight: ${fontWeights.normal};
`;

const StyledInlineLinkButton = styled(InlineLinkButton)`
  font-weight: ${fontWeights.medium};
`;

const StyledTeamInboxDiv = styled.div`
  margin-top: 1em;
`;

/*
 * Props.
 */

export interface DuplicateConversationBannerProps {
  conversationId: number;
  duplicateConversationIds: ReadonlyArray<number>;
  duplicateConversationTeamInboxes: ReadonlyArray<InboxModel>;
  duplicateConversationMessageCount: number;
  onBannerDisplayed: (arg: number) => void;
  onFetchConversationDuplicates: (arg: number) => void;
  onSearchConversationDuplicatesClick: (arg: number) => void;
  onMergeConversationsClick: (arg: ReadonlyArray<number>) => void;
  onLearnMoreClick: (arg: number) => void;
}

/*
 * Component.
 */

export const DuplicateConversationBanner: FC<DuplicateConversationBannerProps> = (props) => {
  const theme = useUserTheme();
  const {
    conversationId,
    duplicateConversationIds,
    duplicateConversationTeamInboxes,
    duplicateConversationMessageCount,
    onBannerDisplayed,
    onFetchConversationDuplicates,
    onSearchConversationDuplicatesClick,
    onMergeConversationsClick,
  } = props;

  const [mergeErrorCode, setMergeErrorCode] = useState<string>();

  useInitialEffect(() => {
    onFetchConversationDuplicates(conversationId);
    onBannerDisplayed(conversationId);
  });

  const [isDialogOpen, toggleIsDialogOpen] = useToggle();

  const onClickView = useCallback(() => {
    onSearchConversationDuplicatesClick(conversationId);
  }, [conversationId, onSearchConversationDuplicatesClick]);

  const onClickMerge = useCallback(async () => {
    // Don't allow the merge button to be clicked twice.
    toggleIsDialogOpen();
    // TODO: Show progress
    try {
      await onMergeConversationsClick([conversationId, ...duplicateConversationIds]);
    } catch (error) {
      const errorCode = (error instanceof HttpResponseError && error.code) || 'unknown';
      setMergeErrorCode(errorCode);
    }
  }, [conversationId, duplicateConversationIds, onMergeConversationsClick, toggleIsDialogOpen]);

  // We want the user to know the total number of copies, this conversation included.
  const totalConversationCount = duplicateConversationIds.length + 1;

  const canMergeConversation = duplicateConversationMessageCount < MAX_MESSAGE_COUNT_TO_MERGE;

  return (
    <StyledStaticViewerBanner iconName="duplicateCopiesSmall" color={PaletteColorsEnum.BLUE}>
      <StyledBannerContent>
        <StyledDarkTextSpan>
          <Plural
            id="activity-banner-duplicates-copycount"
            value={totalConversationCount}
            one={
              <>
                You have <StyledTextStrong>{totalConversationCount} copy</StyledTextStrong> of this
                conversation
              </>
            }
            other={
              <>
                You have <StyledTextStrong>{totalConversationCount} copies</StyledTextStrong> of this
                conversation
              </>
            }
          />
        </StyledDarkTextSpan>
        <StyledInfoIconWrapperDiv>
          <TooltipCoordinator
            persistent
            placement="top"
            iconColor={theme.palette.grey.shade50}
            render={() => renderTooltip(props)}
          />
        </StyledInfoIconWrapperDiv>
      </StyledBannerContent>
      <StyledActionButtonWrapperDiv>
        {canMergeConversation && (
          <StyledStaticViewerBannerAction onClick={toggleIsDialogOpen}>
            <StyledActionTextSpan>
              <Trans id="activity-banner-duplicates-merge">Merge All</Trans>
            </StyledActionTextSpan>
          </StyledStaticViewerBannerAction>
        )}
        <StyledStaticViewerBannerAction onClick={onClickView}>
          <StyledActionTextSpan>
            <Trans id="activity-banner-duplicates-view">View</Trans>
          </StyledActionTextSpan>
        </StyledStaticViewerBannerAction>
      </StyledActionButtonWrapperDiv>
      {isDialogOpen &&
        renderConfirmationDialog(duplicateConversationTeamInboxes, onClickMerge, toggleIsDialogOpen)}
      {mergeErrorCode && renderMergeErrorDialog(mergeErrorCode, () => setMergeErrorCode(undefined))}
    </StyledStaticViewerBanner>
  );
};

function renderTooltip(props: DuplicateConversationBannerProps) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledTooltipTextDiv>
          <Trans id="activity-banner-duplicates-tooltip-text">
            Why do I have multiple copies of the same conversation?
          </Trans>{' '}
          <StyledInlineLinkButton
            color={theme.greys.shade30}
            hoverColor={theme.alphas.white60}
            hasTextDecoration
            onClick={() => props.onLearnMoreClick(props.conversationId)}
          >
            <Trans id="activity-banner-duplicates-tooltip-learnmore">Learn more</Trans>
          </StyledInlineLinkButton>
        </StyledTooltipTextDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderConfirmationDialog(
  duplicateConversationTeamInboxes: ReadonlyArray<InboxModel>,
  onOkClick: () => void,
  onRequestClose: () => void,
) {
  return (
    <OkCancelDialog
      title={<Trans id="activity-banner-duplicates-dialog-title">Merge conversations</Trans>}
      okButtonLabel={<Trans id="activity-banner-duplicates-dialog-merge">Merge</Trans>}
      cancelButtonLabel={<Trans id="activity-banner-duplicates-dialog-cancel">Cancel</Trans>}
      onOkClick={onOkClick}
      onRequestClose={onRequestClose}
      width={400}
    >
      <Trans id="activity-banner-duplicates-dialog-body">
        This will merge all messages and comments into a single conversation. This action cannot be undone.
      </Trans>

      {maybeRenderTeamInbox(duplicateConversationTeamInboxes)}
    </OkCancelDialog>
  );
}

function renderMergeErrorDialog(errorCode: string, onClose: () => void) {
  return (
    <BasicDialog title={<Trans id="conversation-merge_error">Merge failed</Trans>} onRequestClose={onClose}>
      {generateMergeErrorMessage(errorCode)}
    </BasicDialog>
  );
}

function maybeRenderTeamInbox(duplicateConversationTeamInboxes: ReadonlyArray<InboxModel>) {
  if (duplicateConversationTeamInboxes.length < 1) {
    return null;
  }

  return (
    <StyledTeamInboxDiv>
      <Plural
        id="activity-banner-duplicates-dialog-team-inbox"
        value={duplicateConversationTeamInboxes.length}
        one={
          <>
            The merged conversation will be visible in shared inbox{' '}
            <strong>{duplicateConversationTeamInboxes[0].name}</strong> since one copy lives in this inbox.
          </>
        }
        other={
          <>
            The merged conversation will be visible in{' '}
            <strong>{duplicateConversationTeamInboxes.length}</strong> shared inboxes since some copies live
            in these shared inboxes.
          </>
        }
      />
    </StyledTeamInboxDiv>
  );
}
